// ============================================================================
// SCHEDULING STORE — Stage 4 + Post-Consult Completion
//
// Functions:
//   setScheduled(tenantId, referralId, currentWorkflow, appointmentDetails, uid, role)
//     Scheduler books an appointment: READY_TO_SCHEDULE → SCHEDULED
//
//   completeConsult(tenantId, referralId, { closedLoopNote }, uid, role)
//     Specialist completes a consult atomically:
//     SCHEDULED → SEEN → CLOSED_LOOP (two events, one writeBatch)
//     Role: specialist only.
//
//   closeLoop(tenantId, referralId, { closedLoopNote }, uid, role)
//     Specialist admin seals a SEEN referral:
//     SEEN → CLOSED_LOOP (one event, one writeBatch)
//     Role: specialist_admin only.
//
// Firestore paths:
//   tenants/{tenantId}/referrals/{referralId}/workflow/state         (UPDATE)
//   tenants/{tenantId}/referrals/{referralId}/workflowEvents/{eId}  (CREATE ×1 or ×2)
//   tenants/{tenantId}/referrals/{referralId}                        (UPDATE — workflowStatus denorm)
//
// Invariants:
//   - scheduler        : READY_TO_SCHEDULE → SCHEDULED only
//   - specialist        : SCHEDULED → SEEN → CLOSED_LOOP (compound, atomic)
//   - specialist_admin  : SEEN → CLOSED_LOOP only (standalone seal)
//   - FSM + role enforcement via applyStatusTransition (setScheduled) or inline guard
//   - Zero-PHI: no patient name, DOB, MRN stored anywhere in this module
// ============================================================================


import { getApp } from 'firebase/app';
import {
  getFirestore,
  doc,
  getDoc,
  writeBatch,
  serverTimestamp,
  increment,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import {
  buildWorkflowEvent,
  WORKFLOW_STATUS,
  WORKFLOW_EVENT_TYPES,
  RETURN_REASON_CODES,
} from './store.workflow.js';
import { PATHS } from './firestorePaths.js';
import {
  buildReworkRequestDoc,
  REWORK_REQUEST_STATUS,
} from './store.rework.js';
import {
  getSchedulingAvailability,
  isSlotAllowed,
} from './schedulingAvailability.js';
import {
  buildCommunicationDoc,
  COMMUNICATION_PURPOSE,
  RELATED_OBJECT_TYPE,
} from './store.communications.js';

// ── DB singleton ──────────────────────────────────────────────────────────────

function getDb() {
  return getFirestore(getApp());
}

// ── ID generator ──────────────────────────────────────────────────────────────

function generateEventId() {
  return `wfe_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ── Appointment instance ledger ───────────────────────────────────────────────
//
// Stable, non-PHI appointment instance identity. One document per booked
// appointment instance at:
//   tenants/{tenantId}/referrals/{referralId}/appointmentInstances/{appointmentInstanceId}
// A reschedule closes the old instance (RESCHEDULED_OUT) and creates a new one,
// so an appointment outcome cohort can be reconstructed by appointment due date.
// STRICT NON-PHI: no patient name/contact/MRN, no consultantName, no free-text notes.

export const APPOINTMENT_INSTANCE_STATUS = {
  SCHEDULED:          'SCHEDULED',
  SEEN:               'SEEN',
  PATIENT_NO_SHOW:    'PATIENT_NO_SHOW',
  PATIENT_CANCELLED:  'PATIENT_CANCELLED',
  CLINIC_CANCELLED:   'CLINIC_CANCELLED',
  RESCHEDULED_OUT:    'RESCHEDULED_OUT',
};

function generateAppointmentInstanceId() {
  return `apt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Read the referral's specialtyId (non-PHI core doc) for appointment-instance
 * creation. Throws if the referral doc is missing so callers fail fast.
 */
async function readReferralSpecialtyId(db, tenantId, referralId) {
  const snap = await getDoc(doc(db, PATHS.referral(tenantId, referralId)));
  if (!snap.exists()) {
    throw new Error('Referral document not found. Cannot create appointment instance.');
  }
  return snap.data().specialtyId ?? null;
}

/**
 * Enforce Scheduling Availability before any booking/reschedule write.
 * Fails fast (throws) when availability cannot be read, is not configured, or
 * the chosen day/time is outside the allowed window. There is NO fallback to
 * SLA business hours — booking is blocked until availability is configured.
 * @throws {Error}
 */
async function assertSlotAvailable(tenantId, appointmentDate, appointmentTime) {
  // Read error (permission/network) propagates → caller fails fast.
  const { exists, availability } = await getSchedulingAvailability(tenantId);
  if (!exists) {
    throw new Error('Scheduling availability has not been configured for this tenant. A tenant admin must set it before appointments can be booked.');
  }
  if (!isSlotAllowed(availability, appointmentDate, appointmentTime)) {
    throw new Error('Selected date or time is outside the configured scheduling availability.');
  }
}

/**
 * Build a SCHEDULED appointment instance document (structured, non-PHI only).
 * @param {object} params
 * @param {string} params.now  serverTimestamp() sentinel (created/updated/scheduledAt)
 */
function buildAppointmentInstanceDoc({
  appointmentInstanceId,
  tenantId,
  referralId,
  specialtyId,
  consultantId = null,
  appointmentDate,
  appointmentTime = null,
  scheduledByUid,
  rescheduledFromAppointmentInstanceId = null,
  now,
}) {
  return {
    appointmentInstanceId,
    tenantId,
    referralId,
    specialtyId: specialtyId ?? null,
    consultantId: consultantId ?? null,
    appointmentDate,
    appointmentTime: appointmentTime ?? null,
    status: APPOINTMENT_INSTANCE_STATUS.SCHEDULED,
    reasonCategory: null,
    scheduledAt: now,
    scheduledByUid,
    outcomeAt: null,
    outcomeByUid: null,
    rescheduledToAppointmentInstanceId: null,
    rescheduledFromAppointmentInstanceId: rescheduledFromAppointmentInstanceId ?? null,
    createdAt: now,
    updatedAt: now,
    schemaVersion: 1,
  };
}

// ── getBookedSlots ───────────────────────────────────────────────────────────

/**
 * Query booked time slots for a given specialist on a given date.
 *
 * Returns a Set of 24h time strings (e.g. "09:00", "14:30") that are already
 * booked for the given consultantId on appointmentDate within this tenant.
 *
 * Used by BookAppointmentModal to:
 *   1. Hide already-booked time slots in the calendar picker.
 *   2. Re-check for collisions immediately before writing a booking.
 *
 * Query: referral docs where workflowStatus == SCHEDULED, then filter by
 * scheduling.consultantId + scheduling.appointmentDate from workflow/state.
 *
 * Zero-PHI: reads only workflow scheduling metadata, never /phi/**.
 *
 * @param {string} tenantId
 * @param {string} consultantId
 * @param {string} appointmentDate  — YYYY-MM-DD
 * @param {string} [excludeReferralId] — exclude this referral (for reschedule flows)
 * @returns {Promise<Set<string>>}  — Set of 24h time strings already booked
 */
export async function getBookedSlots(tenantId, consultantId, appointmentDate, excludeReferralId = null) {
  if (!tenantId || !consultantId || !appointmentDate) return new Set();

  const db = getDb();

  const refSnap = await getDocs(
    query(
      collection(db, PATHS.col.referrals(tenantId)),
      where('workflowStatus', '==', WORKFLOW_STATUS.SCHEDULED),
    ),
  );

  if (refSnap.empty) return new Set();

  const workflowReads = refSnap.docs
    .filter((d) => !excludeReferralId || d.id !== excludeReferralId)
    .map((d) =>
      getDoc(doc(db, PATHS.workflowState(tenantId, d.id)))
        .then((snap) => (snap.exists() ? snap.data() : null))
        .catch(() => null),
    );

  const workflows = await Promise.all(workflowReads);

  const booked = new Set();
  for (const wf of workflows) {
    if (
      wf?.scheduling?.consultantId === consultantId &&
      wf?.scheduling?.appointmentDate === appointmentDate &&
      wf?.scheduling?.appointmentTime
    ) {
      booked.add(wf.scheduling.appointmentTime);
    }
  }
  return booked;
}

// ── setScheduled ──────────────────────────────────────────────────────────────

/**
 * Book an appointment for a referral (scheduler only).
 *
 * Transitions: READY_TO_SCHEDULE → SCHEDULED
 * FSM + role validation enforced by applyStatusTransition (reads fresh state).
 *
 * Appointment details written atomically with the status change:
 *   scheduling.appointmentDate  — date string (YYYY-MM-DD)
 *   scheduling.appointmentTime  — 24-h time string (HH:MM) — optional
 *   scheduling.consultantId     — consultant uid — optional, non-PHI
 *   scheduling.consultantName   — display name — optional, non-PHI
 *   scheduling.schedulingNote   — internal note — optional, non-PHI
 *   scheduling.scheduledBy      — uid of the scheduler who booked
 *
 * @param {string}   tenantId
 * @param {string}   referralId
 * @param {object}   _currentWorkflow    — ignored; applyStatusTransition reads fresh state
 * @param {object}   appointmentDetails  — { appointmentDate, appointmentTime?, consultantId?, consultantName?, schedulingNote? }
 * @param {string}   uid                 — acting scheduler's uid
 * @param {string}   role                — must be 'scheduler'
 * @throws {Error} if status ≠ READY_TO_SCHEDULE, or role not allowed
 */
export async function setScheduled(tenantId, referralId, _currentWorkflow, appointmentDetails, uid, role) {
  // scheduler is the normal booking role; tenant_admin / platform_admin retain the
  // unrestricted access they had via the prior applyStatusTransition path.
  if (role !== 'scheduler' && role !== 'tenant_admin' && role !== 'platform_admin') {
    throw new Error(`Role "${role}" cannot book an appointment. Expected scheduler.`);
  }

  const {
    appointmentDate = null,
    appointmentTime = null,
    consultantId    = null,
    consultantName  = null,
    locationId      = null,
    schedulingNote  = null,
  } = appointmentDetails ?? {};

  if (!appointmentDate) {
    throw new Error('An appointment date is required to book.');
  }

  const db = getDb();

  // Read fresh state — must be READY_TO_SCHEDULE (FSM guard).
  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const snap     = await getDoc(stateRef);
  if (!snap.exists()) {
    throw new Error('Workflow state document not found. Cannot book appointment.');
  }
  const current    = snap.data();
  const fromStatus = current.status;
  if (fromStatus !== WORKFLOW_STATUS.READY_TO_SCHEDULE) {
    throw new Error(
      `Cannot book: referral is in status "${fromStatus}", expected "${WORKFLOW_STATUS.READY_TO_SCHEDULE}".`
    );
  }

  // Booking constraint: the chosen day/time must satisfy Scheduling Availability.
  await assertSlotAvailable(tenantId, appointmentDate, appointmentTime);

  // specialtyId for the appointment instance (non-PHI). Fails fast if missing.
  const specialtyId = await readReferralSpecialtyId(db, tenantId, referralId);

  const now   = serverTimestamp();
  const batch = writeBatch(db);

  // 1. Create the appointment instance (status SCHEDULED).
  const appointmentInstanceId = generateAppointmentInstanceId();
  const instanceRef = doc(db, PATHS.appointmentInstance(tenantId, referralId, appointmentInstanceId));
  batch.set(instanceRef, buildAppointmentInstanceDoc({
    appointmentInstanceId,
    tenantId,
    referralId,
    specialtyId,
    consultantId,
    appointmentDate,
    appointmentTime,
    scheduledByUid: uid,
    now,
  }));

  // 2. Update workflow/state → SCHEDULED (+ scheduling metadata + instance pointer).
  //    Mirrors applyStatusTransition's SCHEDULED behavior (scheduledAt/By, statusEnteredAt reset).
  batch.update(stateRef, {
    status:                         WORKFLOW_STATUS.SCHEDULED,
    statusUpdatedAt:                now,
    statusEnteredAt:                now,
    lastTouchedAt:                  now,
    updatedAt:                      now,
    updatedByUid:                   uid,
    'scheduling.appointmentDate':   appointmentDate,
    'scheduling.appointmentTime':   appointmentTime,
    'scheduling.consultantId':      consultantId,
    'scheduling.consultantName':    consultantName,
    'scheduling.locationId':        locationId,
    'scheduling.schedulingNote':    schedulingNote,
    'scheduling.scheduledBy':       uid,
    'scheduling.scheduledAt':       now,
    'scheduling.scheduledByUid':    uid,
    currentAppointmentInstanceId:   appointmentInstanceId,
  });

  // 3. Append STATUS_CHANGED event (READY_TO_SCHEDULE → SCHEDULED) — same event the
  //    rollup trigger counts as flow.scheduled + stageTimes.atScheduled + active mirror.
  const eventId  = generateEventId();
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));
  batch.set(eventRef, {
    ...buildWorkflowEvent({
      tenantId,
      referralId,
      eventType:  WORKFLOW_EVENT_TYPES.STATUS_CHANGED,
      fromStatus: WORKFLOW_STATUS.READY_TO_SCHEDULE,
      toStatus:   WORKFLOW_STATUS.SCHEDULED,
      ownerUid:   current.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  role,
    }),
    appointmentInstanceId,
    createdAt: now,
  });

  // 4. Denormalize workflowStatus onto the parent referral doc.
  const referralRef = doc(db, PATHS.referral(tenantId, referralId));
  batch.update(referralRef, {
    workflowStatus:          WORKFLOW_STATUS.SCHEDULED,
    workflowStatusEnteredAt: now,
  });

  await batch.commit();
}

// ── completeConsult ───────────────────────────────────────────────────────────

/**
 * Complete a specialist consult atomically (specialist only).
 *
 * This is a compound transition: SCHEDULED → SEEN → CLOSED_LOOP
 * Both status changes are committed in a single writeBatch with two separate
 * workflowEvents, so the audit trail captures each stage individually.
 *
 * Why not two applyStatusTransition calls?
 *   applyStatusTransition reads fresh Firestore state on every call. After the
 *   first call commits SCHEDULED → SEEN, calling it again for SEEN → CLOSED_LOOP
 *   would require a second round-trip AND would leave a window between the two
 *   writes. One atomic batch avoids both problems.
 *
 * Firestore writes (single batch):
 *   1. workflow/state   UPDATE  — status=CLOSED_LOOP + seenAt + closedAt + closedByUid
 *   2. workflowEvents   CREATE  — SCHEDULED→SEEN event
 *   3. workflowEvents   CREATE  — SEEN→CLOSED_LOOP event
 *   4. referral doc     UPDATE  — workflowStatus denormalization
 *
 * @param {string}   tenantId
 * @param {string}   referralId
 * @param {object}   options            — { closedLoopNote? } (non-PHI text, optional)
 * @param {string}   uid                — acting user's uid
 * @param {string}   role               — 'specialist' or 'specialist_admin'
 * @throws {Error} if status ≠ SCHEDULED, role not allowed, or Firestore write fails
 */
export async function completeConsult(tenantId, referralId, { closedLoopNote = null } = {}, uid, role) {
  // Role guard — specialist only. specialist_admin uses closeLoop() instead.
  if (role !== 'specialist') {
    throw new Error(`Role "${role}" cannot complete a consult. Expected specialist.`);
  }

  const db = getDb();

  // Read fresh workflow state — must be SCHEDULED.
  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const snap     = await getDoc(stateRef);
  if (!snap.exists()) {
    throw new Error('Workflow state document not found. Cannot complete consult.');
  }
  const current    = snap.data();
  const fromStatus = current.status;

  if (fromStatus !== WORKFLOW_STATUS.SCHEDULED) {
    throw new Error(
      `Cannot complete consult: referral is in status "${fromStatus}", expected "${WORKFLOW_STATUS.SCHEDULED}".`
    );
  }

  // Exit Gate: block CLOSED_LOOP unless evidence exists (note OR consult document).
  const hasNote     = typeof closedLoopNote === 'string' && closedLoopNote.trim().length > 0;
  const hasConsultDoc = current.closedLoop?.hasConsultDocument === true;
  if (!hasNote && !hasConsultDoc) {
    throw new Error(
      'Cannot close referral loop: a closing note or an uploaded consult document is required.'
    );
  }

  const now = serverTimestamp();

  // Build the atomic batch.
  const batch = writeBatch(db);

  // 1. Update workflow/state — jump directly to CLOSED_LOOP; record both
  //    seenAt and closedAt so downstream views can surface each timestamp.
  batch.update(stateRef, {
    status:                   WORKFLOW_STATUS.CLOSED_LOOP,
    statusUpdatedAt:          now,
    updatedAt:                now,
    updatedByUid:             uid,
    'scheduling.seenAt':      now,
    'scheduling.seenByUid':   uid,
    'closedLoop.closedAt':    now,
    'closedLoop.closedByUid': uid,
    ...(closedLoopNote ? { 'closedLoop.closedLoopNote': closedLoopNote } : {}),
  });

  // 2. workflowEvent — SCHEDULED → SEEN
  const seenEventId  = generateEventId();
  const seenEventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, seenEventId));
  batch.set(seenEventRef, {
    ...buildWorkflowEvent({
      tenantId,
      referralId,
      eventType:  WORKFLOW_EVENT_TYPES.SEEN_SET,
      fromStatus: WORKFLOW_STATUS.SCHEDULED,
      toStatus:   WORKFLOW_STATUS.SEEN,
      ownerUid:   current.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  role,
    }),
    createdAt: now,
  });

  // 3. workflowEvent — SEEN → CLOSED_LOOP
  const closeEventId  = generateEventId();
  const closeEventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, closeEventId));
  batch.set(closeEventRef, {
    ...buildWorkflowEvent({
      tenantId,
      referralId,
      eventType:  WORKFLOW_EVENT_TYPES.CLOSED_LOOP_SET,
      fromStatus: WORKFLOW_STATUS.SEEN,
      toStatus:   WORKFLOW_STATUS.CLOSED_LOOP,
      ownerUid:   current.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  role,
    }),
    createdAt: now,
  });

  // 4. Appointment instance → SEEN (the appointment was attended). The cohort's
  //    terminal appointment outcome is SEEN; CLOSED_LOOP is a referral-level seal,
  //    not an appointment-instance status. Skipped for legacy referrals with no pointer.
  const instanceId = current.currentAppointmentInstanceId ?? null;
  if (instanceId) {
    const instanceRef = doc(db, PATHS.appointmentInstance(tenantId, referralId, instanceId));
    batch.update(instanceRef, {
      status:       APPOINTMENT_INSTANCE_STATUS.SEEN,
      outcomeAt:    now,
      outcomeByUid: uid,
      updatedAt:    now,
    });
  }

  // 5. Denormalize workflowStatus onto the parent referral doc (mirrors commitWorkflowAction).
  const referralRef = doc(db, PATHS.referral(tenantId, referralId));
  batch.update(referralRef, {
    workflowStatus:          WORKFLOW_STATUS.CLOSED_LOOP,
    workflowStatusEnteredAt: now,
  });

  await batch.commit();
}

// ── markAsSeen ────────────────────────────────────────────────────────────────

/**
 * Mark a scheduled referral as seen by the specialist (specialist only).
 *
 * Transition: SCHEDULED → SEEN
 * FSM + role validation enforced by applyStatusTransition.
 * Automatically stamps scheduling.seenAt and scheduling.seenByUid.
 *
 * @param {string}  tenantId
 * @param {string}  referralId
 * @param {string}  uid   — acting specialist's uid
 * @param {string}  role  — must be 'specialist'
 * @param {object}  [options]
 * @param {string}  [options.trigger] — e.g. 'SPECIALIST_RECONCILIATION'
 * @throws {Error} if status ≠ SCHEDULED, or role not allowed
 */
export async function markAsSeen(tenantId, referralId, uid, role, options = {}) {
  if (role !== 'specialist') {
    throw new Error(`Role "${role}" cannot mark a referral as seen. Expected specialist.`);
  }

  const db = getDb();
  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const snap     = await getDoc(stateRef);
  if (!snap.exists()) {
    throw new Error('Workflow state document not found. Cannot mark as seen.');
  }
  const current = snap.data();
  if (current.status !== WORKFLOW_STATUS.SCHEDULED) {
    throw new Error(
      `Cannot mark seen: referral is in status "${current.status}", expected "${WORKFLOW_STATUS.SCHEDULED}".`
    );
  }

  const now   = serverTimestamp();
  const batch = writeBatch(db);
  const instanceId = current.currentAppointmentInstanceId ?? null;

  // 1. workflow/state → SEEN (mirrors applyStatusTransition SEEN behavior).
  batch.update(stateRef, {
    status:                 WORKFLOW_STATUS.SEEN,
    statusUpdatedAt:        now,
    statusEnteredAt:        now,
    lastTouchedAt:          now,
    updatedAt:              now,
    updatedByUid:           uid,
    'scheduling.seenAt':    now,
    'scheduling.seenByUid': uid,
  });

  // 2. Appointment instance → SEEN (when the referral has a live instance; legacy
  //    referrals booked before the ledger have no pointer and are skipped).
  if (instanceId) {
    const instanceRef = doc(db, PATHS.appointmentInstance(tenantId, referralId, instanceId));
    batch.update(instanceRef, {
      status:       APPOINTMENT_INSTANCE_STATUS.SEEN,
      outcomeAt:    now,
      outcomeByUid: uid,
      updatedAt:    now,
    });
  }

  // 3. Append STATUS_CHANGED event (SCHEDULED → SEEN).
  const eventId  = generateEventId();
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));
  batch.set(eventRef, {
    ...buildWorkflowEvent({
      tenantId,
      referralId,
      eventType:  WORKFLOW_EVENT_TYPES.STATUS_CHANGED,
      fromStatus: WORKFLOW_STATUS.SCHEDULED,
      toStatus:   WORKFLOW_STATUS.SEEN,
      ownerUid:   current.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  role,
    }),
    ...(instanceId ? { appointmentInstanceId: instanceId } : {}),
    ...(options.trigger ? { trigger: options.trigger } : {}),
    createdAt: now,
  });

  // 4. Denormalize workflowStatus onto the parent referral doc.
  const referralRef = doc(db, PATHS.referral(tenantId, referralId));
  batch.update(referralRef, {
    workflowStatus:          WORKFLOW_STATUS.SEEN,
    workflowStatusEnteredAt: now,
  });

  await batch.commit();
}

// ── closeLoop ─────────────────────────────────────────────────────────────────

/**
 * Seal a SEEN referral into CLOSED_LOOP (specialist_admin only).
 *
 * This is a single-step transition: SEEN → CLOSED_LOOP
 * One workflowEvent is created alongside the state update, committed atomically.
 *
 * Firestore writes (single batch):
 *   1. workflow/state   UPDATE  — status=CLOSED_LOOP + closedAt + closedByUid + closedLoopNote
 *   2. workflowEvents   CREATE  — SEEN→CLOSED_LOOP event
 *   3. referral doc     UPDATE  — workflowStatus denormalization
 *
 * @param {string}   tenantId
 * @param {string}   referralId
 * @param {object}   options            — { closedLoopNote } (required, non-PHI text)
 * @param {string}   uid                — acting user's uid
 * @param {string}   role               — must be 'specialist_admin'
 * @throws {Error} if status ≠ SEEN, role not allowed, closedLoopNote missing, or Firestore write fails
 */
export async function closeLoop(tenantId, referralId, { closedLoopNote = '' } = {}, uid, role) {
  // Role guard — specialist or specialist_admin.
  // specialist reaches SEEN via markAsSeen(), then closes here.
  // specialist_admin owns the standalone SEEN → CLOSED_LOOP seal.
  if (role !== 'specialist' && role !== 'specialist_admin') {
    throw new Error(`Role "${role}" cannot close a loop. Expected specialist or specialist_admin.`);
  }

  // Normalise note — specialist_admin UI enforces non-empty at the form level;
  // specialist note is optional (same contract as completeConsult).
  const note = typeof closedLoopNote === 'string' ? closedLoopNote.trim() : '';

  const db = getDb();

  // Read fresh workflow state — must be SEEN.
  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const snap     = await getDoc(stateRef);
  if (!snap.exists()) {
    throw new Error('Workflow state document not found. Cannot close loop.');
  }
  const current    = snap.data();
  const fromStatus = current.status;

  if (fromStatus !== WORKFLOW_STATUS.SEEN) {
    throw new Error(
      `Cannot close loop: referral is in status "${fromStatus}", expected "${WORKFLOW_STATUS.SEEN}".`
    );
  }

  // Exit Gate: block CLOSED_LOOP unless evidence exists (note OR consult document).
  const hasNote     = note.length > 0;
  const hasConsultDoc = current.closedLoop?.hasConsultDocument === true;
  if (!hasNote && !hasConsultDoc) {
    throw new Error(
      'Cannot close referral loop: a closing note or an uploaded consult document is required.'
    );
  }

  const now = serverTimestamp();

  // Build the atomic batch.
  const batch = writeBatch(db);

  // 1. Update workflow/state — transition to CLOSED_LOOP.
  batch.update(stateRef, {
    status:                   WORKFLOW_STATUS.CLOSED_LOOP,
    statusUpdatedAt:          now,
    updatedAt:                now,
    updatedByUid:             uid,
    'closedLoop.closedAt':    now,
    'closedLoop.closedByUid': uid,
    ...(note ? { 'closedLoop.closedLoopNote': note } : {}),
  });

  // 2. workflowEvent — SEEN → CLOSED_LOOP
  const eventId  = generateEventId();
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));
  batch.set(eventRef, {
    ...buildWorkflowEvent({
      tenantId,
      referralId,
      eventType:  WORKFLOW_EVENT_TYPES.CLOSED_LOOP_SET,
      fromStatus: WORKFLOW_STATUS.SEEN,
      toStatus:   WORKFLOW_STATUS.CLOSED_LOOP,
      ownerUid:   current.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  role,
    }),
    createdAt: now,
  });

  // 3. Denormalize workflowStatus onto the parent referral doc.
  const referralRef = doc(db, PATHS.referral(tenantId, referralId));
  batch.update(referralRef, {
    workflowStatus:          WORKFLOW_STATUS.CLOSED_LOOP,
    workflowStatusEnteredAt: now,
  });

  await batch.commit();
}

// ── returnToIntake ─────────────────────────────────────────────────────────

/**
 * Return a READY_TO_SCHEDULE referral back to intake (scheduler only).
 *
 * Transition: READY_TO_SCHEDULE → NEEDS_INFO
 * Creates rework request docs for each selected item type and stamps
 * returnToIntake metadata on workflow/state so intake can distinguish
 * scheduler-returned items from generic NEEDS_INFO.
 *
 * Atomic batch writes:
 *   1. workflow/state   UPDATE  — status=NEEDS_INFO + returnToIntake metadata + rework.missingItemsCount
 *   2. workflowEvents   CREATE  — RETURNED_TO_INTAKE event
 *   3. reworkRequests    CREATE  — one doc per selectedItemType
 *   4. referral doc      UPDATE  — workflowStatus denormalization
 *
 * @param {string}   tenantId
 * @param {string}   referralId
 * @param {object}   options
 * @param {string}   options.reasonCode       — RETURN_REASON_CODES value (required)
 * @param {string}   [options.note]           — optional non-PHI note
 * @param {string[]} options.selectedItemTypes — REWORK_ITEM_TYPE values (at least one)
 * @param {string}   uid                      — acting scheduler's uid
 * @param {string}   role                     — must be 'scheduler'
 * @throws {Error} if status ≠ READY_TO_SCHEDULE, role not allowed, or missing required params
 */
export async function returnToIntake(
  tenantId,
  referralId,
  { reasonCode, note = '', selectedItemTypes = [], receiverUid, receiverRole = 'intake_coordinator', returnedByName = null },
  uid,
  role,
) {
  // Role guard
  if (role !== 'scheduler') {
    throw new Error(`Role "${role}" cannot return to intake. Expected scheduler.`);
  }
  if (!reasonCode || !RETURN_REASON_CODES[reasonCode]) {
    throw new Error(`Invalid return reason code: "${reasonCode}".`);
  }
  if (!selectedItemTypes || selectedItemTypes.length === 0) {
    throw new Error('Select at least one missing item type for the rework request.');
  }
  // Receiver required — return must create both movement and attention.
  if (!receiverUid) {
    throw new Error('Select an Intake Coordinator before returning this referral.');
  }
  if (receiverUid === uid) {
    throw new Error('You cannot return this referral to yourself.');
  }

  const db = getDb();

  // Read fresh workflow state — must be READY_TO_SCHEDULE.
  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const snap     = await getDoc(stateRef);
  if (!snap.exists()) {
    throw new Error('Workflow state document not found. Cannot return to intake.');
  }
  const current    = snap.data();
  const fromStatus = current.status;

  if (fromStatus !== WORKFLOW_STATUS.READY_TO_SCHEDULE) {
    throw new Error(
      `Cannot return to intake: referral is in status "${fromStatus}", expected "${WORKFLOW_STATUS.READY_TO_SCHEDULE}".`
    );
  }

  const now        = serverTimestamp();
  const noteClean  = typeof note === 'string' ? note.trim() : '';
  const batch      = writeBatch(db);

  // Pre-generate the handoff ping id so we can stamp it onto workflow/state and
  // create the communication doc in the SAME batch (movement + attention together).
  const pingId = generateEventId();

  // 1. Update workflow/state — NEEDS_INFO + returnToIntake metadata
  batch.update(stateRef, {
    status:              WORKFLOW_STATUS.NEEDS_INFO,
    statusUpdatedAt:     now,
    statusEnteredAt:     now,
    lastTouchedAt:       now,
    updatedAt:           now,
    updatedByUid:        uid,
    'returnToIntake.active':       true,
    'returnToIntake.reasonCode':   reasonCode,
    'returnToIntake.note':         noteClean,
    'returnToIntake.selectedItemTypes': selectedItemTypes,
    'returnToIntake.returnedBy':   uid,
    'returnToIntake.returnedByName': returnedByName,
    'returnToIntake.returnedAt':   now,
    'returnToIntake.returnedFrom': 'SCHEDULING',
    'returnToIntake.receiverUid':  receiverUid,
    'returnToIntake.pingId':       pingId,
    'rework.missingItemsCount':    increment(selectedItemTypes.length),
  });

  // 2. Workflow event — RETURNED_TO_INTAKE
  const eventId  = generateEventId();
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));
  batch.set(eventRef, {
    ...buildWorkflowEvent({
      tenantId,
      referralId,
      eventType:  WORKFLOW_EVENT_TYPES.RETURNED_TO_INTAKE,
      fromStatus: WORKFLOW_STATUS.READY_TO_SCHEDULE,
      toStatus:   WORKFLOW_STATUS.NEEDS_INFO,
      ownerUid:   current.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  role,
    }),
    reasonCode,
    note: noteClean,
    selectedItemTypes,
    createdAt: now,
  });

  // 3. Create one reworkRequest doc per selected item type
  for (const itemType of selectedItemTypes) {
    const requestId  = generateEventId(); // reuse ID generator for unique IDs
    const requestRef = doc(db, PATHS.reworkRequest(tenantId, referralId, requestId));
    const requestDoc = buildReworkRequestDoc({
      tenantId,
      referralId,
      requestId,
      itemType,
      createdByUid:  uid,
      createdByRole: role,
    });
    batch.set(requestRef, { ...requestDoc, createdAt: now, updatedAt: now });
  }

  // 4. Denormalize workflowStatus + the GP-inbox discriminator onto the parent
  //    referral doc. awaitingGpDocs = true because this return creates >=1 OPEN
  //    document request the GP must upload (cleared by the attachment-ready Cloud
  //    Function when the last request is received). affectedKeys() allowlist in
  //    firestore.rules permits exactly these three keys.
  const referralRef = doc(db, PATHS.referral(tenantId, referralId));
  batch.update(referralRef, {
    workflowStatus:          WORKFLOW_STATUS.NEEDS_INFO,
    workflowStatusEnteredAt: now,
    awaitingGpDocs:          true,
  });

  // 5. RETURN_TO_INTAKE handoff ping — same batch (movement + attention atomic).
  //    Throws (before commit) on PHI/length in the note. Non-PHI only.
  const commRef  = doc(db, PATHS.communication(tenantId, referralId, pingId));
  const commDoc  = buildCommunicationDoc({
    tenantId,
    referralId,
    communicationId:   pingId,
    purpose:           COMMUNICATION_PURPOSE.RETURN_TO_INTAKE,
    message:           noteClean || null,
    relatedObjectType: RELATED_OBJECT_TYPE.WORKFLOW_STATE,
    relatedObjectId:   referralId,
    senderUid:         uid,
    senderRole:        role,            // 'scheduler'
    recipientUid:      receiverUid,
    recipientRole:     receiverRole,    // 'intake_coordinator'
    now,
  });
  batch.set(commRef, commDoc);

  await batch.commit();
  return { pingId };
}

// ── Scheduling Escape Hatch (3 actions) ──────────────────────────────────────

export const CANCEL_REASON = {
  PATIENT_REQUEST:        'PATIENT_REQUEST',
  PATIENT_NO_SHOW:        'PATIENT_NO_SHOW',
  PATIENT_DECLINED:       'PATIENT_DECLINED',
  CLINIC_CAPACITY:        'CLINIC_CAPACITY',
  CLINIC_PROVIDER_UNAVAIL:'CLINIC_PROVIDER_UNAVAIL',
  CLINIC_ADMIN_ERROR:     'CLINIC_ADMIN_ERROR',
  OTHER:                  'OTHER',
};

export const CANCEL_REASON_LABELS = {
  PATIENT_REQUEST:        'Patient requested cancellation',
  PATIENT_NO_SHOW:        'Patient no-show',
  PATIENT_DECLINED:       'Patient declined appointment',
  CLINIC_CAPACITY:        'Clinic capacity issue',
  CLINIC_PROVIDER_UNAVAIL:'Provider unavailable',
  CLINIC_ADMIN_ERROR:     'Administrative error',
  OTHER:                  'Other (see note)',
};

function snapshotAppointment(scheduling) {
  return {
    appointmentDate: scheduling?.appointmentDate ?? null,
    appointmentTime: scheduling?.appointmentTime ?? null,
    consultantId:    scheduling?.consultantId ?? null,
    consultantName:  scheduling?.consultantName ?? null,
    locationId:      scheduling?.locationId ?? null,
    schedulingNote:  scheduling?.schedulingNote ?? null,
    scheduledBy:     scheduling?.scheduledBy ?? null,
    scheduledAt:     scheduling?.scheduledAt ?? null,
  };
}

function readFreshScheduledState(db, tenantId, referralId) {
  return getDoc(doc(db, PATHS.workflowState(tenantId, referralId))).then(snap => {
    if (!snap.exists()) {
      throw new Error('Workflow state document not found.');
    }
    const data = snap.data();
    if (data.status !== WORKFLOW_STATUS.SCHEDULED) {
      throw new Error(
        `Referral is in status "${data.status}", expected "${WORKFLOW_STATUS.SCHEDULED}".`
      );
    }
    return { stateRef: doc(db, PATHS.workflowState(tenantId, referralId)), current: data };
  });
}

/**
 * Reschedule an already-scheduled appointment (scheduler only).
 *
 * Transition: SCHEDULED → SCHEDULED (self-transition with updated appointment details).
 * Snapshots the previous appointment into lastScheduledAppointment before overwriting.
 *
 * @param {string}   tenantId
 * @param {string}   referralId
 * @param {object}   newAppointment  — { appointmentDate, appointmentTime?, consultantId?, consultantName?, locationId?, schedulingNote? }
 * @param {string}   rescheduleNote  — required non-PHI reason
 * @param {string}   uid             — acting scheduler's uid
 * @param {string}   role            — must be 'scheduler'
 */
export async function rescheduleAppointment(tenantId, referralId, newAppointment, rescheduleNote, uid, role) {
  if (role !== 'scheduler') {
    throw new Error(`Role "${role}" cannot reschedule. Expected scheduler.`);
  }
  if (!rescheduleNote || typeof rescheduleNote !== 'string' || !rescheduleNote.trim()) {
    throw new Error('A reschedule note is required.');
  }
  if (!newAppointment?.appointmentDate) {
    throw new Error('New appointment date is required.');
  }

  const db = getDb();
  const { stateRef, current } = await readFreshScheduledState(db, tenantId, referralId);

  // specialtyId for the NEW appointment instance (non-PHI). Fails fast if missing.
  const specialtyId = await readReferralSpecialtyId(db, tenantId, referralId);

  const now       = serverTimestamp();
  const noteClean = rescheduleNote.trim();
  const batch     = writeBatch(db);
  const prev      = snapshotAppointment(current.scheduling);

  const {
    appointmentDate,
    appointmentTime = null,
    consultantId    = null,
    consultantName  = null,
    locationId      = null,
    schedulingNote  = null,
  } = newAppointment;

  // Booking constraint: the NEW slot must satisfy Scheduling Availability —
  // unless it is identical to the current appointment (a legacy appointment that
  // already sits outside availability stays reschedulable without being forced
  // to move). Any real move to a different day/time is validated.
  const sameAsCurrent = appointmentDate === (current.scheduling?.appointmentDate ?? null)
    && appointmentTime === (current.scheduling?.appointmentTime ?? null);
  if (!sameAsCurrent) {
    await assertSlotAvailable(tenantId, appointmentDate, appointmentTime);
  }

  // Appointment instance ledger: close the old instance as RESCHEDULED_OUT and
  // create a new SCHEDULED instance. The old instance keeps its original date so
  // the cohort attributes it to the date it was originally due. Legacy referrals
  // without a pointer simply start a fresh instance (no old instance to close).
  const oldInstanceId = current.currentAppointmentInstanceId ?? null;
  const newInstanceId = generateAppointmentInstanceId();
  const newInstanceRef = doc(db, PATHS.appointmentInstance(tenantId, referralId, newInstanceId));

  if (oldInstanceId) {
    const oldInstanceRef = doc(db, PATHS.appointmentInstance(tenantId, referralId, oldInstanceId));
    batch.update(oldInstanceRef, {
      status:                              APPOINTMENT_INSTANCE_STATUS.RESCHEDULED_OUT,
      outcomeAt:                           now,
      outcomeByUid:                        uid,
      reasonCategory:                      null, // no structured reschedule-reason enum
      rescheduledToAppointmentInstanceId:  newInstanceId,
      updatedAt:                           now,
    });
  }

  batch.set(newInstanceRef, buildAppointmentInstanceDoc({
    appointmentInstanceId: newInstanceId,
    tenantId,
    referralId,
    specialtyId,
    consultantId,
    appointmentDate,
    appointmentTime,
    scheduledByUid: uid,
    rescheduledFromAppointmentInstanceId: oldInstanceId,
    now,
  }));

  batch.update(stateRef, {
    status:                       WORKFLOW_STATUS.SCHEDULED,
    statusUpdatedAt:              now,
    updatedAt:                    now,
    updatedByUid:                 uid,
    'scheduling.appointmentDate': appointmentDate,
    'scheduling.appointmentTime': appointmentTime,
    'scheduling.consultantId':    consultantId,
    'scheduling.consultantName':  consultantName,
    'scheduling.locationId':      locationId,
    'scheduling.schedulingNote':  schedulingNote,
    'scheduling.scheduledBy':     uid,
    'scheduling.scheduledAt':     now,
    lastScheduledAppointment:     prev,
    currentAppointmentInstanceId: newInstanceId,
    'lastModifyAction.action':    'RESCHEDULE',
    'lastModifyAction.at':        now,
    'lastModifyAction.byUid':     uid,
    'lastModifyAction.note':      noteClean,
  });

  const eventId  = generateEventId();
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));
  batch.set(eventRef, {
    ...buildWorkflowEvent({
      tenantId,
      referralId,
      eventType:  WORKFLOW_EVENT_TYPES.APPOINTMENT_RESCHEDULED,
      fromStatus: WORKFLOW_STATUS.SCHEDULED,
      toStatus:   WORKFLOW_STATUS.SCHEDULED,
      ownerUid:   current.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  role,
    }),
    // ── Rollup-safe normalized fields (structured, non-PHI) ──────────────────
    appointmentExceptionKind: 'rescheduled',
    reasonCategory:           null, // no structured reschedule-reason enum exists; note is free text and excluded from rollups
    previousAppointmentDate:  prev.appointmentDate ?? null,
    previousAppointmentTime:  prev.appointmentTime ?? null,
    newAppointmentDate:       appointmentDate,
    newAppointmentTime:       appointmentTime,
    consultantId:             consultantId ?? null,
    previousAppointmentInstanceId: oldInstanceId,
    newAppointmentInstanceId:      newInstanceId,
    // ── Legacy / UI fields (NOT used by rollups) ─────────────────────────────
    previousAppointment: prev,
    note: noteClean,
    createdAt: now,
  });

  const referralRef = doc(db, PATHS.referral(tenantId, referralId));
  batch.update(referralRef, {
    workflowStatusEnteredAt: now,
  });

  await batch.commit();
}

/**
 * Cancel a scheduled appointment at patient's request (scheduler only).
 *
 * Transition: SCHEDULED → CANCELLED (terminal — no care delivered).
 *
 * @param {string}   tenantId
 * @param {string}   referralId
 * @param {string}   cancelReason  — CANCEL_REASON enum value
 * @param {string}   cancelNote    — required non-PHI note
 * @param {string}   uid           — acting scheduler's uid
 * @param {string}   role          — must be 'scheduler'
 */
export async function cancelPatientRequest(tenantId, referralId, cancelReason, cancelNote, uid, role) {
  if (role !== 'scheduler') {
    throw new Error(`Role "${role}" cannot cancel. Expected scheduler.`);
  }
  if (!cancelReason || !CANCEL_REASON[cancelReason]) {
    throw new Error(`Invalid cancel reason: "${cancelReason}".`);
  }
  if (!cancelNote || typeof cancelNote !== 'string' || !cancelNote.trim()) {
    throw new Error('A cancellation note is required.');
  }

  const db = getDb();
  const { stateRef, current } = await readFreshScheduledState(db, tenantId, referralId);

  const now       = serverTimestamp();
  const noteClean = cancelNote.trim();
  const batch     = writeBatch(db);
  const prev      = snapshotAppointment(current.scheduling);

  const instanceId = current.currentAppointmentInstanceId ?? null;

  batch.update(stateRef, {
    status:                       WORKFLOW_STATUS.CANCELLED,
    statusUpdatedAt:              now,
    statusEnteredAt:              now,
    updatedAt:                    now,
    updatedByUid:                 uid,
    lastScheduledAppointment:     prev,
    'cancelled.cancelledAt':      now,
    'cancelled.cancelledByUid':   uid,
    'cancelled.cancelReason':     cancelReason,
    'cancelled.cancelNote':       noteClean,
    'cancelled.cancelType':       'PATIENT',
    'lastModifyAction.action':    'CANCEL_PATIENT',
    'lastModifyAction.at':        now,
    'lastModifyAction.byUid':     uid,
    'lastModifyAction.note':      noteClean,
  });

  // Appointment instance → PATIENT_CANCELLED (skipped for legacy referrals
  // booked before the ledger, which carry no instance pointer).
  if (instanceId) {
    const instanceRef = doc(db, PATHS.appointmentInstance(tenantId, referralId, instanceId));
    batch.update(instanceRef, {
      status:         APPOINTMENT_INSTANCE_STATUS.PATIENT_CANCELLED,
      outcomeAt:      now,
      outcomeByUid:   uid,
      reasonCategory: cancelReason, // structured CANCEL_REASON enum
      updatedAt:      now,
    });
  }

  const eventId  = generateEventId();
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));
  batch.set(eventRef, {
    ...buildWorkflowEvent({
      tenantId,
      referralId,
      eventType:  WORKFLOW_EVENT_TYPES.APPOINTMENT_CANCELLED,
      fromStatus: WORKFLOW_STATUS.SCHEDULED,
      toStatus:   WORKFLOW_STATUS.CANCELLED,
      ownerUid:   current.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  role,
    }),
    // ── Rollup-safe normalized fields (structured, non-PHI) ──────────────────
    appointmentExceptionKind: 'patient_cancelled',
    reasonCategory:           cancelReason, // structured CANCEL_REASON enum
    previousAppointmentDate:  prev.appointmentDate ?? null,
    previousAppointmentTime:  prev.appointmentTime ?? null,
    newAppointmentDate:       null,
    newAppointmentTime:       null,
    consultantId:             prev.consultantId ?? null,
    ...(instanceId ? { appointmentInstanceId: instanceId } : {}),
    // ── Legacy / UI fields (NOT used by rollups) ─────────────────────────────
    cancelReason,
    cancelType: 'PATIENT',
    note: noteClean,
    previousAppointment: prev,
    createdAt: now,
  });

  const referralRef = doc(db, PATHS.referral(tenantId, referralId));
  batch.update(referralRef, {
    workflowStatus:          WORKFLOW_STATUS.CANCELLED,
    workflowStatusEnteredAt: now,
  });

  await batch.commit();
}

/**
 * Cancel a scheduled appointment for clinic-side reasons (scheduler only).
 *
 * Transition: SCHEDULED → READY_TO_SCHEDULE (returns for re-booking).
 *
 * @param {string}   tenantId
 * @param {string}   referralId
 * @param {string}   cancelReason  — CANCEL_REASON enum value
 * @param {string}   cancelNote    — required non-PHI note
 * @param {string}   uid           — acting scheduler's uid
 * @param {string}   role          — must be 'scheduler'
 */
export async function cancelClinicRequest(tenantId, referralId, cancelReason, cancelNote, uid, role) {
  if (role !== 'scheduler') {
    throw new Error(`Role "${role}" cannot cancel. Expected scheduler.`);
  }
  if (!cancelReason || !CANCEL_REASON[cancelReason]) {
    throw new Error(`Invalid cancel reason: "${cancelReason}".`);
  }
  if (!cancelNote || typeof cancelNote !== 'string' || !cancelNote.trim()) {
    throw new Error('A cancellation note is required.');
  }

  const db = getDb();
  const { stateRef, current } = await readFreshScheduledState(db, tenantId, referralId);

  const now       = serverTimestamp();
  const noteClean = cancelNote.trim();
  const batch     = writeBatch(db);
  const prev      = snapshotAppointment(current.scheduling);

  const instanceId = current.currentAppointmentInstanceId ?? null;

  batch.update(stateRef, {
    status:                       WORKFLOW_STATUS.READY_TO_SCHEDULE,
    statusUpdatedAt:              now,
    statusEnteredAt:              now,
    updatedAt:                    now,
    updatedByUid:                 uid,
    lastScheduledAppointment:     prev,
    // Clinic cancellation returns the referral to scheduling; the appointment
    // instance is no longer active. The next booking creates a new instance.
    currentAppointmentInstanceId: null,
    'lastModifyAction.action':    'CANCEL_CLINIC',
    'lastModifyAction.at':        now,
    'lastModifyAction.byUid':     uid,
    'lastModifyAction.note':      noteClean,
  });

  // Appointment instance → CLINIC_CANCELLED (skipped for legacy referrals
  // booked before the ledger, which carry no instance pointer).
  if (instanceId) {
    const instanceRef = doc(db, PATHS.appointmentInstance(tenantId, referralId, instanceId));
    batch.update(instanceRef, {
      status:         APPOINTMENT_INSTANCE_STATUS.CLINIC_CANCELLED,
      outcomeAt:      now,
      outcomeByUid:   uid,
      reasonCategory: cancelReason, // structured CANCEL_REASON enum
      updatedAt:      now,
    });
  }

  const eventId  = generateEventId();
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));
  batch.set(eventRef, {
    ...buildWorkflowEvent({
      tenantId,
      referralId,
      eventType:  WORKFLOW_EVENT_TYPES.APPOINTMENT_CANCELLED,
      fromStatus: WORKFLOW_STATUS.SCHEDULED,
      toStatus:   WORKFLOW_STATUS.READY_TO_SCHEDULE,
      ownerUid:   current.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  role,
    }),
    // ── Rollup-safe normalized fields (structured, non-PHI) ──────────────────
    appointmentExceptionKind: 'clinic_cancelled',
    reasonCategory:           cancelReason, // structured CANCEL_REASON enum
    previousAppointmentDate:  prev.appointmentDate ?? null,
    previousAppointmentTime:  prev.appointmentTime ?? null,
    newAppointmentDate:       null,
    newAppointmentTime:       null,
    consultantId:             prev.consultantId ?? null,
    ...(instanceId ? { appointmentInstanceId: instanceId } : {}),
    // ── Legacy / UI fields (NOT used by rollups) ─────────────────────────────
    cancelReason,
    cancelType: 'CLINIC',
    note: noteClean,
    previousAppointment: prev,
    createdAt: now,
  });

  const referralRef = doc(db, PATHS.referral(tenantId, referralId));
  batch.update(referralRef, {
    workflowStatus:          WORKFLOW_STATUS.READY_TO_SCHEDULE,
    workflowStatusEnteredAt: now,
  });

  await batch.commit();
}

/**
 * Mark a scheduled appointment as No-Show (scheduler only).
 *
 * Transition: SCHEDULED → NO_SHOW (terminal — patient did not attend).
 * Preserves appointment metadata in lastScheduledAppointment snapshot.
 *
 * @param {string} tenantId
 * @param {string} referralId
 * @param {string} uid   — acting user's uid
 * @param {string} role  — must be 'scheduler' or 'specialist'
 * @param {object} [options]
 * @param {string} [options.trigger] — e.g. 'SPECIALIST_RECONCILIATION'
 */
export async function markAsNoShow(tenantId, referralId, uid, role, options = {}) {
  if (role !== 'scheduler' && role !== 'specialist') {
    throw new Error(`Role "${role}" cannot mark no-show. Expected scheduler or specialist.`);
  }

  const db = getDb();
  const { stateRef, current } = await readFreshScheduledState(db, tenantId, referralId);

  const now   = serverTimestamp();
  const batch = writeBatch(db);
  const prev  = snapshotAppointment(current.scheduling);
  const instanceId = current.currentAppointmentInstanceId ?? null;

  batch.update(stateRef, {
    status:                       WORKFLOW_STATUS.NO_SHOW,
    statusUpdatedAt:              now,
    statusEnteredAt:              now,
    updatedAt:                    now,
    updatedByUid:                 uid,
    lastScheduledAppointment:     prev,
    'noShow.noShowAt':            now,
    'noShow.noShowMarkedBy':      uid,
    'lastModifyAction.action':    'NO_SHOW',
    'lastModifyAction.at':        now,
    'lastModifyAction.byUid':     uid,
    'lastModifyAction.note':      null,
  });

  // Appointment instance → PATIENT_NO_SHOW (skipped for legacy referrals booked
  // before the ledger, which carry no instance pointer). NO_SHOW stays terminal.
  if (instanceId) {
    const instanceRef = doc(db, PATHS.appointmentInstance(tenantId, referralId, instanceId));
    batch.update(instanceRef, {
      status:         APPOINTMENT_INSTANCE_STATUS.PATIENT_NO_SHOW,
      outcomeAt:      now,
      outcomeByUid:   uid,
      reasonCategory: CANCEL_REASON.PATIENT_NO_SHOW,
      updatedAt:      now,
    });
  }

  const eventId  = generateEventId();
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));
  const eventPayload = {
    ...buildWorkflowEvent({
      tenantId,
      referralId,
      eventType:  WORKFLOW_EVENT_TYPES.NO_SHOW_SET,
      fromStatus: WORKFLOW_STATUS.SCHEDULED,
      toStatus:   WORKFLOW_STATUS.NO_SHOW,
      ownerUid:   current.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  role,
    }),
    // ── Rollup-safe normalized fields (structured, non-PHI) ──────────────────
    appointmentExceptionKind: 'patient_no_show',
    reasonCategory:           CANCEL_REASON.PATIENT_NO_SHOW, // 'PATIENT_NO_SHOW'
    previousAppointmentDate:  prev.appointmentDate ?? null,
    previousAppointmentTime:  prev.appointmentTime ?? null,
    newAppointmentDate:       null,
    newAppointmentTime:       null,
    consultantId:             prev.consultantId ?? null,
    ...(instanceId ? { appointmentInstanceId: instanceId } : {}),
    // ── Legacy / UI fields (NOT used by rollups) ─────────────────────────────
    previousAppointment: prev,
    createdAt: now,
  };
  if (options.trigger) eventPayload.trigger = options.trigger;
  batch.set(eventRef, eventPayload);

  const referralRef = doc(db, PATHS.referral(tenantId, referralId));
  batch.update(referralRef, {
    workflowStatus:          WORKFLOW_STATUS.NO_SHOW,
    workflowStatusEnteredAt: now,
  });

  await batch.commit();
}
